import { useState, useEffect } from "react";
import { Select } from "antd";
import { uploadFile } from "../../../../services/files";
import {
  departments as departmentsApi,
  jobTypes as jobTypesApi,
  dataPermissions,
} from "../../../../services/admin";
import { listProjects } from "../../../../services/projects";
import { listWorkStages, type WorkStageItem } from "../../../../services/workStages";
import type { DepartmentItem, DataPermissionRule } from "../../../../services/admin";
import RIcon from "../../../../components/RIcon";

interface UploadModalProps {
  onClose: () => void;
  onUploaded: () => void;
}


interface SimpleOption {
  id: number;
  name: string;
}

export default function UploadModal({ onClose, onUploaded }: UploadModalProps) {
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);


  const [dataPermissionRules, setDataPermissionRules] = useState<DataPermissionRule[]>([]);
  const [departmentList, setDepartmentList] = useState<DepartmentItem[]>([]);
  const [dataPermissionRuleIds, setDataPermissionRuleIds] = useState<number[]>([]);
  const [departmentId, setDepartmentId] = useState<number | undefined>();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [jobTypeList, setJobTypeList] = useState<SimpleOption[]>([]);
  const [jobTypeId, setJobTypeId] = useState<number | undefined>();
  const [stageList, setStageList] = useState<SimpleOption[]>([]);
  const [workStageId, setWorkStageId] = useState<number | undefined>();
  const [projectList, setProjectList] = useState<SimpleOption[]>([]);
  const [projectId, setProjectId] = useState<number | undefined>();
  const [category, setCategory] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [downloadable, setDownloadable] = useState(false);


  const PRESET_CATEGORIES = ["模板文件", "BOM", "质量标准", "作业指导书", "检验规程", "技术要求", "变更控制", "其他"];

  useEffect(() => {

    departmentsApi.list().then(setDepartmentList).catch(() => {});
    jobTypesApi.list().then(setJobTypeList).catch(() => {});
    listProjects().then(setProjectList).catch(() => {});

    dataPermissions.listRules().then(setDataPermissionRules).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (jobTypeId) {
      listWorkStages(undefined, jobTypeId).then((stages: WorkStageItem[]) => {
        if (cancelled) return;
        const filtered = stages.filter(
          (stage) => !jobTypeId || !stage.job_type_id || stage.job_type_id === jobTypeId
        );
        setStageList(filtered.map((stage) => ({ id: stage.id, name: stage.name })));
        if (workStageId && !filtered.some((stage) => stage.id === workStageId)) {
          setWorkStageId(undefined);
        }
      }).catch(() => {
        if (!cancelled) {
          setStageList([]);
          setWorkStageId(undefined);
        }
      });
    } else {
      setStageList([]);
      setWorkStageId(undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [jobTypeId, workStageId]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    setFile(e.dataTransfer.files[0] ?? null);
    setErrors((prev) => ({ ...prev, file: "" }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!file) errs.file = "请上传文件";
    return errs;
  };

  const handleClose = () => {
    if (loading && !window.confirm("文件正在上传中，确定要关闭窗口吗？")) return;
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    try {
      const finalCategory = category === "custom" ? customCategory : category;
      await uploadFile(file!, "public", undefined, {
        department_id: departmentId,
        job_type_id: jobTypeId,
        work_stage_id: workStageId,
        project_id: projectId,
        category: finalCategory || undefined,
        data_permission_rule_ids: dataPermissionRuleIds,
        downloadable,
      });
      onUploaded();
      onClose();
    } catch (err) {
      setErrors({ file: err instanceof Error ? err.message : "上传失败" });
    } finally {
      setLoading(false);
    }
  };

  const labelCls = "block text-sm font-medium text-[#1A2B3C] mb-1.5";
  const selectStyle = { width: "100%" } as const;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-semibold text-[#1A2B3C]">上传文件</h3>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer">
            <RIcon name="close-line" className="text-[#718096]" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          {/* 文件选择 */}
          <div>
            <label className={labelCls}>选择文件</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-6 text-center transition-colors cursor-pointer ${
                dragOver ? "border-[#0EA5A0] bg-[#0EA5A0]/5" : errors.file ? "border-red-300 bg-red-50/30" : "border-gray-200 hover:border-[#0EA5A0]/50"
              }`}
              onClick={() => document.getElementById("file-input")?.click()}
            >
              {file ? (
                <div className="flex items-center gap-3 justify-center max-w-full">
                  <RIcon name="file-line" className="text-[#0EA5A0] text-lg shrink-0" />
                  <span className="text-sm text-[#1A2B3C] truncate max-w-[320px]" title={file.name}>{file.name}</span>
                  <button type="button" onClick={(ev) => { ev.stopPropagation(); setFile(null); }} className="text-[#718096] hover:text-red-500 cursor-pointer shrink-0">
                    <RIcon name="close-line" className="text-sm" />
                  </button>
                </div>
              ) : (
                <>
                  <RIcon name="upload-cloud-2-line" className="text-3xl text-[#718096] mb-2 block" />
                  <p className="text-sm text-[#718096]">点击或拖拽文件到此处上传</p>
                  <p className="text-xs text-[#718096]/60 mt-1">支持 PDF、Word、Excel、PPT 等格式</p>
                </>
              )}
              <input type="file" id="file-input" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setErrors((prev) => ({ ...prev, file: "" })); }} />
            </div>
            {errors.file && <p className="text-red-500 text-xs mt-1">{errors.file}</p>}
          </div>

          <div>
            <label className={labelCls}>所属部门</label>
            <Select
              showSearch
              allowClear
              placeholder="请选择部门"
              style={selectStyle}
              value={departmentId}
              onChange={(v) => setDepartmentId(v as number | undefined)}
              optionFilterProp="label"
              options={departmentList.map((d) => ({ value: d.id, label: d.name }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>工种</label>
              <Select
                showSearch
                allowClear
                placeholder="请选择工种"
                style={selectStyle}
                value={jobTypeId}
                onChange={(v) => setJobTypeId(v as number | undefined)}
                optionFilterProp="label"
                options={jobTypeList.map((jt) => ({ value: jt.id, label: jt.name }))}
              />
            </div>
            <div>
              <label className={labelCls}>业务阶段</label>
              <Select
                showSearch
                allowClear
                placeholder={jobTypeId ? "请选择阶段" : "请先选择工种"}
                style={selectStyle}
                value={workStageId}
                disabled={!jobTypeId}
                onChange={(v) => setWorkStageId(v as number | undefined)}
                optionFilterProp="label"
                options={stageList.map((s) => ({ value: s.id, label: s.name }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>所属项目</label>
              <Select
                showSearch
                allowClear
                placeholder="请选择项目"
                style={selectStyle}
                value={projectId}
                onChange={(v) => setProjectId(v as number | undefined)}
                optionFilterProp="label"
                options={projectList.map((p) => ({ value: p.id, label: p.name }))}
              />
            </div>
            <div>
              <label className={labelCls}>文件分类</label>
              <Select
                showSearch
                allowClear
                placeholder="请选择分类"
                style={selectStyle}
                value={category || undefined}
                onChange={(v) => {
                  const next = (v ?? "") as string;
                  setCategory(next);
                  if (next !== "custom") setCustomCategory("");
                }}
                optionFilterProp="label"
                options={[
                  ...PRESET_CATEGORIES.map((c) => ({ value: c, label: c })),
                  { value: "custom", label: "自定义分类..." },
                ]}
              />
            </div>
          </div>

          {category === "custom" && (
            <div>
              <label className={labelCls}>自定义分类名称</label>
              <input
                type="text"
                className="w-full border rounded-lg px-3 h-10 text-sm outline-none border-gray-200 focus:border-[#0EA5A0] bg-white"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                placeholder="输入分类名称"
              />
            </div>
          )}

          <div>
            <label className={labelCls}>数据权限规则（可多选；不勾选 = 不绑定规则，默认全公司可见）</label>
            <Select
              mode="multiple"
              showSearch
              allowClear
              placeholder="搜索并选择规则（多选）"
              style={selectStyle}
              value={dataPermissionRuleIds}
              onChange={(v) => setDataPermissionRuleIds(v as number[])}
              optionFilterProp="label"
              options={dataPermissionRules.map((rule) => ({ value: rule.id, label: rule.name }))}
            />
          </div>

          <p className="text-xs text-[#718096]">文件访问范围以所选数据权限规则为准；多规则之间为并集（任一规则放行即可访问）。</p>

          <div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                className="accent-[#0EA5A0] w-4 h-4"
                checked={downloadable}
                onChange={(e) => setDownloadable(e.target.checked)}
              />
              <span className="text-sm text-[#1A2B3C]">允许下载</span>
              <span className="text-xs text-[#718096]">（取消勾选则即便有"下载文件"角色权限也无法下载本文件）</span>
            </label>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={handleClose} className="flex-1 h-10 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:bg-gray-50 cursor-pointer whitespace-nowrap">取消</button>
            <button type="submit" disabled={loading} className="flex-1 h-10 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm font-medium disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap">
              {loading ? <><RIcon name="loader-4-line" className="animate-spin" />上传中...</> : "确认上传"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
